import { useState } from "react";
import Button from "@components/ui/Button";
import ErrorBanner from "@components/shared/ErrorBanner";
import {
  RULE_KINDS, WEEKDAYS, describeRule, draftFromWeekdays, draftProblems,
  summariseWeekdays, weekdaysFromDraft,
} from "@components/scheduling/settings/scheduleRules";

const selectClass =
  "border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

const SOURCE_LABEL = {
  manual: { text: "Set by staff", className: "bg-blue-100 text-blue-800" },
  learned: { text: "Learned from the book", className: "bg-gray-100 text-gray-700" },
  none: { text: "No pattern", className: "bg-amber-100 text-amber-800" },
};

/**
 * One provider's working days: a shut summary line with the rota in force,
 * and an editor that opens in place. Owns the draft and its validation;
 * `onSave(weekdays | null)` does the write and may reject with an Error whose
 * message is shown beside the button. `leading` renders before the badge
 * (the admin drawer puts the role and default controls there).
 * `disabled` only stops the editor opening; an open editor is governed by
 * its own save.
 *
 * By default the days are guessed from the last three months of
 * appointments; an admin can type the real schedule here and it replaces the
 * guess for that provider. Hours are optional per day: none means the whole
 * open day. The PMS code is shown because Fathom lists some dentists twice
 * under different codes.
 */
export default function ProviderScheduleRow({ provider: p, onSave, leading = null, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [problems, setProblems] = useState([]);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const openEditor = () => {
    // Editing starts from what is in force: the manual rota if there is one,
    // else the learned guess, so the admin corrects rather than retypes.
    setDraft(draftFromWeekdays(p.manual_schedule ?? p.learned_schedule));
    setProblems([]);
    setSaveError(null);
    setOpen(true);
  };

  const close = () => { setOpen(false); setDraft(null); setProblems([]); setSaveError(null); };

  const setLine = (key, patch) => {
    // The complaint was about the draft as it stood; editing it makes the
    // message stale, so it goes rather than sitting there contradicted.
    setProblems([]);
    setDraft((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
  };

  const setHours = (key, update) =>
    setLine(key, { hours: update(draft[key].hours || []) });
  const addWindow = (key) => setHours(key, (hours) => [...hours, { start: "", end: "" }]);
  const removeWindow = (key, index) => setHours(key, (hours) => hours.filter((_, i) => i !== index));
  const setWindow = (key, index, patch) =>
    setHours(key, (hours) => hours.map((w, i) => (i === index ? { ...w, ...patch } : w)));

  const write = async (weekdays) => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(weekdays);
      close();
    } catch (e) {
      setSaveError(e.message || "Could not save the working days");
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    const found = draftProblems(draft);
    setProblems(found);
    if (found.length) return;
    write(weekdaysFromDraft(draft));
  };

  const badge = SOURCE_LABEL[p.schedule_source] || SOURCE_LABEL.none;
  // What is in force, so a shut row still says which days are worked.
  const summary = summariseWeekdays(p.manual_schedule ?? p.learned_schedule);

  return (
    <li className="px-3 py-2 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-gray-900">{p.name}</span>
        <span className="text-xs text-gray-500">{p.external_provider_id}</span>
        {leading}
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
          {badge.text}
        </span>
        {!open && summary && (
          <span className="text-xs text-gray-500">{summary}</span>
        )}
        <span className="ml-auto">
          {open
            ? <Button color="light" size="sm" onClick={close} disabled={saving}
                      aria-label={`Cancel editing ${p.name}`}>Cancel</Button>
            : <Button color="light" size="sm" onClick={openEditor} disabled={disabled}
                      aria-label={`Edit working days for ${p.name} (${p.external_provider_id})`}>
                Edit working days
              </Button>}
        </span>
      </div>

      {open && draft && (
        <fieldset className="min-w-0 space-y-2"
                  aria-label={`Working days for ${p.name} (${p.external_provider_id})`}>
          {p.learned_schedule == null && (
            <p className="text-xs text-gray-500">
              No pattern could be learned from the book for this provider.
            </p>
          )}
          {WEEKDAYS.map(({ key, label }) => {
            const line = draft[key];
            return (
              <div key={key} className="flex flex-wrap items-center gap-2">
                <label htmlFor={`${p.external_provider_id}-${key}`}
                       className="w-24 text-sm text-gray-700">{label}</label>
                <select id={`${p.external_provider_id}-${key}`} className={selectClass}
                        value={line.kind} disabled={saving}
                        onChange={(e) => setLine(key, { kind: e.target.value })}>
                  {RULE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
                {line.kind === "biweekly" && (
                  <input type="date" className={selectClass} value={line.anchor}
                         aria-label={`${label} start date`} disabled={saving}
                         onChange={(e) => setLine(key, { anchor: e.target.value })} />
                )}
                {line.kind === "nth_weekday" && (
                  <select className={selectClass} value={line.n} disabled={saving}
                          aria-label={`${label} occurrence`}
                          onChange={(e) => setLine(key, { n: e.target.value })}>
                    {["1", "2", "3", "4", "5"].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                )}
                {p.learned_schedule != null && (
                  <span className="text-xs text-gray-500">
                    learned: {describeRule(p.learned_schedule[key])}
                  </span>
                )}
                {line.kind !== "off" && (
                  <HoursControls providerId={p.external_provider_id} dayKey={key}
                                 label={label} hours={line.hours || []} disabled={saving}
                                 onAdd={() => addWindow(key)}
                                 onRemove={(i) => removeWindow(key, i)}
                                 onChange={(i, patch) => setWindow(key, i, patch)} />
                )}
              </div>
            );
          })}
          {problems.map((text) => (
            <p key={text} className="text-xs text-red-600">{text}</p>
          ))}
          <ErrorBanner message={saveError} onDismiss={() => setSaveError(null)} />
          <div className="flex flex-wrap items-center justify-end gap-2">
            {p.manual_schedule != null && (
              <Button color="light" size="sm" disabled={saving} onClick={() => write(null)}
                      aria-label={`Use learned pattern for ${p.name}`}>
                Use learned pattern
              </Button>
            )}
            <Button color="primary" size="sm" disabled={saving} onClick={save}
                    aria-label={`Save working days for ${p.name}`}>
              {saving ? "Saving…" : "Save working days"}
            </Button>
          </div>
        </fieldset>
      )}
    </li>
  );
}

/**
 * The hours worked on one weekday: "All day" until the admin sets a window,
 * then one row per window with a remove button and a way to add another,
 * for split shifts. Removing the last window returns the day to all day.
 */
function HoursControls({ providerId, dayKey, label, hours, disabled, onAdd, onRemove, onChange }) {
  if (hours.length === 0) {
    return (
      <span className="flex items-center gap-2 text-xs text-gray-500">
        All day
        <Button color="light" size="sm" disabled={disabled} onClick={onAdd}
                aria-label={`Set ${label} hours`}>Set hours</Button>
      </span>
    );
  }
  return (
    <div className="flex w-full flex-wrap items-center gap-2 pl-24 sm:w-auto sm:pl-0">
      {hours.map((w, i) => (
        <span key={i} className="flex items-center gap-1">
          <input type="time" className={selectClass} value={w.start} disabled={disabled}
                 id={`${providerId}-${dayKey}-hours-${i}-start`}
                 aria-label={`${label} hours ${i + 1} start`}
                 onChange={(e) => onChange(i, { start: e.target.value })} />
          <span className="text-xs text-gray-500">to</span>
          <input type="time" className={selectClass} value={w.end} disabled={disabled}
                 id={`${providerId}-${dayKey}-hours-${i}-end`}
                 aria-label={`${label} hours ${i + 1} end`}
                 onChange={(e) => onChange(i, { end: e.target.value })} />
          <button type="button" disabled={disabled} onClick={() => onRemove(i)}
                  className="px-1 text-sm text-gray-400 hover:text-red-600 disabled:opacity-50"
                  aria-label={`Remove ${label} hours ${i + 1}`}>×</button>
        </span>
      ))}
      <Button color="light" size="sm" disabled={disabled} onClick={onAdd}
              aria-label={`Add ${label} hours`}>Add hours</Button>
    </div>
  );
}
